import { Alert, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LogOnMount } from '@mightyhive/analytics';
import { AppGradientSurface, ConfirmDialog } from '@mightyhive/commoncomponents';
import { APP_LAYOUT_SIZES, getAppTokens } from '@mightyhive/theme';

import { useUser } from '../context/UserContext';
import { CLIENTS_PAGE_LAYOUT_SIZES } from './clients/clientsTokens';
import { ClientListPanel } from './clients/components/ClientListPanel';
import { ClientsPageHeader } from './clients/components/ClientsPageHeader';
import { MemberPanel } from './clients/components/MemberPanel';
import { useClientsPage } from './clients/useClientsPage';

const CLIENTS_SUBTITLE = 'Manage client tenants and member access.';

export function Clients(): JSX.Element {
  const theme = useTheme();
  const appTokens = getAppTokens(theme);
  const { isInternalUser } = useUser();
  const page = useClientsPage();

  if (!isInternalUser) {
    return (
      <AppGradientSurface maxWidth={APP_LAYOUT_SIZES.hubMaxWidth}>
        <LogOnMount eventName="Page View: Clients" />
        <Alert severity="warning">Client management is available to internal users.</Alert>
      </AppGradientSurface>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        px: { xs: 2, sm: `${APP_LAYOUT_SIZES.contentPaddingX}px` },
        py: { xs: 4, sm: `${APP_LAYOUT_SIZES.contentPaddingY}px` },
        boxSizing: 'border-box',
        background: appTokens.pageGradient,
        backgroundColor: appTokens.pageGradientFallback,
      }}
    >
      <LogOnMount eventName="Page View: Clients" />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
          rowGap: `${CLIENTS_PAGE_LAYOUT_SIZES.headerToContentGap}px`,
          width: '100%',
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <ClientsPageHeader subtitle={CLIENTS_SUBTITLE} />
          {page.error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {page.error}
            </Alert>
          ) : null}
        </Box>

        <Box
          sx={{
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: `${CLIENTS_PAGE_LAYOUT_SIZES.leftPanelWidth}px minmax(0, 1fr)`,
            },
            gridTemplateRows: { xs: 'auto 1fr', lg: '1fr' },
            columnGap: `${CLIENTS_PAGE_LAYOUT_SIZES.columnGap}px`,
            rowGap: `${CLIENTS_PAGE_LAYOUT_SIZES.columnGap}px`,
            alignItems: 'stretch',
          }}
        >
          <ClientListPanel
            clientListPanelRef={page.clientListPanelRef}
            clientListToolbarRef={page.clientListToolbarRef}
            clientName={page.clientName}
            clientPage={page.clientPage}
            clientPageCount={page.clientPageCount}
            clientSearch={page.clientSearch}
            clients={page.clients}
            filteredClientsCount={page.filteredClients.length}
            isPoweruser={page.isPoweruser}
            loadingClients={page.loadingClients}
            onClientNameChange={page.setClientName}
            onClientPageChange={page.setClientPage}
            onClientSearchChange={page.setClientSearch}
            onCreateClient={(event) => void page.createClient(event)}
            onSelectClient={page.setSelectedClientUuid}
            pagedClients={page.pagedClients}
            saving={page.saving}
            selectedClientUuid={page.selectedClientUuid}
          />

          <MemberPanel
            filteredMembersCount={page.filteredMembers.length}
            loadingMembers={page.loadingMembers}
            memberEmail={page.memberEmail}
            memberPage={page.memberPage}
            memberRowsPerPage={page.memberRowsPerPage}
            members={page.members}
            memberSearch={page.memberSearch}
            onAddMember={(event) => void page.addMember(event)}
            onMemberEmailChange={page.setMemberEmail}
            onMemberPageChange={page.setMemberPage}
            onMemberRowsPerPageChange={page.setMemberRowsPerPage}
            onMemberSearchChange={page.setMemberSearch}
            onRemoveMember={page.setMemberToRemove}
            pagedMembers={page.pagedMembers}
            saving={page.saving}
            selectedClient={page.selectedClient}
          />
        </Box>
      </Box>

      <ConfirmDialog
        open={page.memberToRemove !== null}
        title="Remove member?"
        message={
          page.memberToRemove
            ? `Remove ${page.memberToRemove.email} from ${page.selectedClient?.name ?? 'this client'}? They will lose access to this client's experiments. This does not delete their account.`
            : ''
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        destructive
        loading={page.saving}
        onCancel={() => {
          if (!page.saving) page.setMemberToRemove(null);
        }}
        onConfirm={() => void page.confirmRemoveMember()}
      />
    </Box>
  );
}
